import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import PageCardHeader from "components/Card/PageCardHeader.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import Icon from "@material-ui/core/Icon";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  programmeName: { fontWeight: "700", color: "#1a237e" },
  codeBadge: { background: "#e8eaf6", color: "#1a237e", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "700" }
};

const BLANK = { name: "", code: "", duration: "2", level: "Certificate", departmentId: "" };

class ProgrammeList extends Component {
  state = {
    programmes: [], departments: [],
    loading: true, saving: false,
    open: false, editing: null,
    form: { ...BLANK },
    error: null
  };

  componentDidMount() { this.fetchData(); }

  fetchData = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get("/programmes?limit=100"),
        api.get("/departments?limit=100").catch(() => ({ data: { data: [] } }))
      ]);
      const pd = pRes.data.data || pRes.data;
      const dd = dRes.data.data || dRes.data;
      this.setState({
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        departments: Array.isArray(dd) ? dd : dd.data || [],
        loading: false
      });
    } catch (e) { this.setState({ loading: false }); }
  };

  openNew = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });
  openEdit = (p) => this.setState({ open: true, editing: p.id, form: { name: p.name, code: p.code, duration: String(p.duration), level: p.level, departmentId: p.departmentId || "" }, error: null });
  handleClose = () => this.setState({ open: false, editing: null, error: null });
  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name || !form.code) { this.setState({ error: "Name and code are required." }); return; }
    this.setState({ saving: true });
    try {
      if (editing) await api.put(`/programmes/${editing}`, form);
      else await api.post("/programmes", form);
      this.setState({ open: false, saving: false });
      this.fetchData();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { programmes, departments, loading, open, form, saving, error } = this.state;

    const columns = [
      {
        id: "code",
        header: "Code",
        accessorFn: p => p.code,
        cell: info => <span className={classes.codeBadge}>{info.getValue()}</span>
      },
      {
        id: "name",
        header: "Programme Name",
        accessorFn: p => p.name,
        cell: info => <span className={classes.programmeName}>{info.getValue()}</span>
      },
      { id: "level", header: "Level", accessorFn: p => p.level },
      {
        id: "duration",
        header: "Duration",
        accessorFn: p => `${p.duration} year${p.duration > 1 ? "s" : ""}`
      },
      { id: "department", header: "Department", accessorFn: p => p.department?.name || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)} style={{ padding: "6px" }}>
            <Icon style={{ fontSize: "20px" }}>edit</Icon>
          </Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Academic Programmes</h1>
              <p className={classes.heroSub}>
                Define and manage academic programmes, levels, and department affiliations.
              </p>
              <div style={{ marginTop: "20px" }}>
                <Button color="white" onClick={this.openNew}>
                  <Icon style={{ marginRight: "8px" }}>add</Icon> New Programme
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e3f2fd", color: "#1565c0" }}>
                <Icon>school</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{programmes.length}</p>
                <p className={classes.statLabel}>Total Programmes</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                <Icon>assignment_turned_in</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{programmes.filter(p => p.level === "Diploma").length}</p>
                <p className={classes.statLabel}>Diploma Courses</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#fff8e1", color: "#e65100" }}>
                <Icon>category</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{new Set(programmes.map(p => p.level)).size}</p>
                <p className={classes.statLabel}>Levels Defined</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <DataTable
            columns={columns}
            data={programmes}
            isLoading={loading}
            emptyState="No programmes found."
          />
        </GridItem>

        <Dialog open={open} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogHeader title={this.state.editing ? "Edit Programme" : "New Programme"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Programme Name *" id="pname" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.name, onChange: this.handleChange("name") }} />
            <CustomInput labelText="Code (e.g. PTC-ECE) *" id="pcode" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.code, onChange: this.handleChange("code") }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Duration (Years)</InputLabel>
              <Select value={form.duration} onChange={this.handleChange("duration")}>
                {[1, 2, 3].map(y => <MenuItem key={y} value={String(y)}>{y} Year{y > 1 ? "s" : ""}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Level</InputLabel>
              <Select value={form.level} onChange={this.handleChange("level")}>
                {["Certificate", "Diploma", "Degree"].map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            {departments.length > 0 && (
              <FormControl className={classes.formControl}>
                <InputLabel>Department</InputLabel>
                <Select value={form.departmentId} onChange={this.handleChange("departmentId")}>
                  <MenuItem value="">— None —</MenuItem>
                  {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={this.handleClose} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

ProgrammeList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ProgrammeList));
